"use client";

import * as React from "react";
import { cn } from "../../../lib/utils";
import type { Environment } from "../../../types";
import { BackIcon, CloseIcon } from "../../wallet-info/components/icons";
import {
  onePopLandingUrl,
  oneUsdBridgeUrl,
  type OnePopLandingPage,
} from "../landing";
import type {
  OnePopActivityItem,
  OnePopActivityStatus,
  OnePopSummary,
} from "../types";
import {
  OnePopActivityIcon,
  OnePopChevronRightIcon,
  OnePopClaimIcon,
  OnePopHandleIcon,
  OnePopHeroArt,
  OnePopInstantIcon,
  OnePopLogo,
  OnePopRefundIcon,
  OnePopSendIcon,
  XBadgeIcon,
} from "./icons";
import "../styles/onepop.css";

/** 리스트 미리보기에 노출하는 최대 활동 수. 나머지는 "See all activity". */
const RECENT_ACTIVITY_LIMIT = 6;

const STATUS_LABEL: Record<OnePopActivityStatus, string> = {
  claimed: "Claimed",
  waiting: "Waiting",
  refunded: "Refunded",
  ready: "Ready",
};

/**
 * raw(wei-scale) 금액을 소수점 2자리 **버림**으로 표기한다. 전 과정을 BigInt로
 * 유지해 2^53 이상에서도 정밀도가 깨지지 않는다 (CLAUDE.md §4). 잔액은 음수가
 * 없으므로 BigInt 나눗셈의 0 방향 절사 = floor.
 */
function formatAmount2(raw: string, decimals: number): string {
  let value: bigint;
  try {
    value = BigInt(raw || "0");
  } catch {
    return "0.00";
  }
  const negative = value < 0n;
  if (negative) value = -value;

  let whole: bigint;
  let cents: bigint;
  if (decimals <= 2) {
    const unit = 10n ** BigInt(decimals);
    whole = value / unit;
    cents = (value % unit) * 10n ** BigInt(2 - decimals);
  } else {
    const hundredths = value / 10n ** BigInt(decimals - 2); // 2자리 초과분 버림
    whole = hundredths / 100n;
    cents = hundredths % 100n;
  }
  return `${negative ? "-" : ""}${groupInteger(whole.toString())}.${cents
    .toString()
    .padStart(2, "0")}`;
}

/**
 * 정수부 천단위 콤마. 코드베이스 관례를 따라 정규식(백트래킹) 대신 선형 루프.
 */
function groupInteger(intPart: string): string {
  let grouped = "";
  for (let i = 0; i < intPart.length; i++) {
    const fromEnd = intPart.length - i;
    if (i > 0 && fromEnd % 3 === 0) grouped += ",";
    grouped += intPart[i];
  }
  return grouped;
}

/** unix timestamp(초 또는 밀리초) → "2h ago" 형태의 상대 시간. */
function formatRelativeTime(timestamp: number): string {
  if (!timestamp) return "";
  const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diffSec < 60) return "just now";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month}mo ago`;
  return `${Math.floor(month / 12)}y ago`;
}

/** `@handle` 표기 — 호출측이 `@`를 붙여 보내도 중복되지 않게 한다. */
function withAt(handle: string): string {
  const trimmed = handle.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
}

export interface OnePopBodyProps {
  /** ONEpop 서비스 웹 딥링크의 환경. 미지정 시 production. */
  env?: Environment;
  /** Available 카드에 표시할 ONEUSD 잔액 (표시용 포맷 완료 문자열). */
  balanceDisplay?: string;
  /** Available 카드의 토큰 아이콘. 미지정 시 아이콘 자리를 비운다. */
  balanceIconUrl?: string;
  /** 잔액 조회 중이면 스켈레톤. */
  isBalanceLoading?: boolean;
  /** 호스트가 `@nexus-cross/pop`으로 조회해 주입하는 상태. 미주입 = 빈 상태. */
  summary?: OnePopSummary;
  /** 브랜드 워드마크 이미지. 미지정 시 인라인 SVG 재현본. */
  logoSrc?: string;
  /** 히어로 일러스트 이미지. 미지정 시 인라인 SVG 재현본. */
  heroSrc?: string;
  onBack: () => void;
  onClose: () => void;
  /**
   * Send POP 카드 + "Send your first POP!" CTA override. 미주입 시 ONEpop 웹
   * 센드 페이지(`/pop`)를 새 탭으로 연다. 다른 액션도 같은 규칙 — 전부 서비스
   * 페이지 이동이라 disabled 상태가 없다.
   */
  onSend?: () => void;
  /** Claim POPs 카드 + 클레임 배너 override. 기본 `/pop/claim`. */
  onClaim?: () => void;
  /**
   * Activity 카드 + "See all activity" override. 기본 `/pop/activity`.
   * Activity 카드는 `summary.xConnected` 가 true 일 때만 렌더된다.
   */
  onActivity?: () => void;
  /**
   * 활동 화면으로 나가는 모든 경로(Activity 카드 / `See all activity` /
   * `N replies waiting` 배너)에서 **추가로** 호출되는 알림 훅. `onActivity` 와
   * 달리 기본 동작을 대체하지 않는다 — 답장을 확인 처리해
   * `repliesWaitingCount` 를 0 으로 떨어뜨리는 용도다.
   */
  onRepliesSeen?: () => void;
  /** "You might have POPs waiting" 배너 override. 기본 `/pop/board`. */
  onBoard?: () => void;
  /** "Get ONEUSD ›" override. 기본 게임토큰 브리지(`/gametoken/bridge`). */
  onGetToken?: () => void;
  /**
   * "Get ONEUSD ›" 자리를 통째로 대체하는 노드. 지갑을 떠나지 않고 Get ONEUSD
   * 위젯(Swap / Bridge / Transfer 선택 모달)을 그 자리에서 열기 위한 슬롯이다 —
   * 위젯은 wagmi 어댑터가 필요해 dapp-ui 가 직접 마운트할 수 없으므로,
   * `@nexus-cross/connect-kit-react` 가 `<CrossGetOneUsd mode="button"
   * buttonComponent={OnePopGetTokenTrigger}>` 를 주입한다.
   *
   * 주입되면 `onGetToken`/기본 딥링크는 쓰이지 않는다.
   */
  getTokenSlot?: React.ReactNode;
}

export function OnePopBody({
  env = "production",
  balanceDisplay,
  balanceIconUrl,
  isBalanceLoading,
  summary,
  logoSrc,
  heroSrc,
  onBack,
  onClose,
  onSend,
  onClaim,
  onActivity,
  onRepliesSeen,
  onBoard,
  onGetToken,
  getTokenSlot,
}: OnePopBodyProps) {
  const activity = summary?.activity ?? [];
  const hasActivity = activity.length > 0;
  const claimableCount = summary?.claimableCount ?? 0;
  const repliesWaitingCount = summary?.repliesWaitingCount ?? 0;
  const isLoading = summary?.isLoading ?? false;
  const xConnected = summary?.xConnected ?? false;

  // 모든 액션의 기본 동작은 ONEpop 서비스 페이지 딥링크다. override 콜백이
  // 있으면 그것만 실행한다.
  const openLanding = React.useCallback(
    (page: OnePopLandingPage) => {
      window.open(onePopLandingUrl(env, page), "_blank", "noopener,noreferrer");
    },
    [env],
  );
  const handleSend = React.useCallback(
    () => (onSend ? onSend() : openLanding("send")),
    [onSend, openLanding],
  );
  const handleClaim = React.useCallback(
    () => (onClaim ? onClaim() : openLanding("claim")),
    [onClaim, openLanding],
  );
  const handleActivity = React.useCallback(() => {
    // 활동 화면에 답장이 보이므로, 나가는 시점에 확인 처리한다. 목적지 이동과
    // 독립이라 override 여부와 무관하게 항상 알린다.
    onRepliesSeen?.();
    if (onActivity) return onActivity();
    openLanding("activity");
  }, [onActivity, onRepliesSeen, openLanding]);
  const handleBoard = React.useCallback(
    () => (onBoard ? onBoard() : openLanding("board")),
    [onBoard, openLanding],
  );
  const handleGetToken = React.useCallback(() => {
    if (onGetToken) return onGetToken();
    window.open(oneUsdBridgeUrl(env), "_blank", "noopener,noreferrer");
  }, [onGetToken, env]);

  const claimableTotal = React.useMemo(() => {
    if (!summary || claimableCount <= 0) return null;
    return formatAmount2(
      summary.claimableTotalRaw,
      summary.claimableDecimals ?? 18,
    );
  }, [summary, claimableCount]);

  const recentActivity = React.useMemo(
    () => activity.slice(0, RECENT_ACTIVITY_LIMIT),
    [activity],
  );

  const handleImageError = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.style.display = "none";
    },
    [],
  );

  return (
    <>
      <div className="wi-sticky-header">
        <div className="wi-header wi-header--receive">
          <button
            type="button"
            className="wi-header-back"
            onClick={onBack}
            aria-label="Back"
          >
            <BackIcon size={24} />
          </button>
          <span className="wi-op-header-title">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="ONEpop"
                className="wi-op-logo-img"
                height={24}
                draggable={false}
                onError={handleImageError}
              />
            ) : (
              <OnePopLogo className="wi-op-logo" />
            )}
          </span>
          <button
            type="button"
            className="wi-header-action"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon size={24} />
          </button>
        </div>
        <hr className="wi-divider" />
      </div>

      <div className="wi-op-scroll" data-vaul-no-drag>
        <section className="wi-op-hero">
          <div className="wi-op-hero-copy">
            <h2 className="wi-op-hero-title">
              <span>Send money with</span>
              <span className="wi-op-hero-title-row">
                just an
                <XBadgeIcon size={20} className="wi-op-x-badge" />
                @handle.
              </span>
            </h2>
            <p className="wi-op-hero-sub">
              They don&apos;t even need a wallet yet.
            </p>
          </div>
          <div className="wi-op-hero-art" aria-hidden>
            {heroSrc ? (
              <img
                src={heroSrc}
                alt=""
                className="wi-op-hero-img"
                draggable={false}
                onError={handleImageError}
              />
            ) : (
              <OnePopHeroArt />
            )}
          </div>
        </section>

        <section className="wi-op-panel">
          <div className="wi-op-balance-card">
            <div className="wi-op-balance-head">
              <span className="wi-op-balance-label">Available</span>
              {getTokenSlot ?? (
                <button
                  type="button"
                  className="wi-op-link-btn"
                  onClick={handleGetToken}
                  data-track="onepop_get_token"
                >
                  <span>Get ONEUSD</span>
                  <OnePopChevronRightIcon size={12} />
                </button>
              )}
            </div>
            <div className="wi-op-balance-body">
              {isBalanceLoading ? (
                <div
                  className="wi-skeleton-block"
                  style={{ width: 140, height: 32 }}
                />
              ) : (
                <>
                  {balanceIconUrl && (
                    <img
                      src={balanceIconUrl}
                      alt=""
                      aria-hidden
                      className="wi-op-balance-icon"
                      width={24}
                      height={24}
                      draggable={false}
                      onError={handleImageError}
                    />
                  )}
                  <span className="wi-op-balance-value">
                    {balanceDisplay ?? "0.00"}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="wi-op-action-row" aria-label="ONEpop actions">
            <button
              type="button"
              className="wi-op-action-card"
              onClick={handleSend}
              aria-label="Send POP"
              data-track="onepop_send"
            >
              <OnePopSendIcon size={24} />
              <span>Send POP</span>
            </button>
            <button
              type="button"
              className="wi-op-action-card"
              onClick={handleClaim}
              aria-label="Claim POPs"
              data-track="onepop_claim"
            >
              <OnePopClaimIcon size={24} />
              <span>Claim POPs</span>
            </button>
            {/* 수신 내역은 X 핸들 기준이라 X 연결이 있어야 의미가 있다. */}
            {xConnected && (
              <button
                type="button"
                className="wi-op-action-card"
                onClick={handleActivity}
                aria-label="Activity"
                data-track="onepop_activity"
              >
                <OnePopActivityIcon size={24} />
                <span>Activity</span>
              </button>
            )}
          </div>
        </section>

        <section className="wi-op-cta-row">
          {isLoading ? (
            <div
              className="wi-skeleton-block wi-op-cta-skeleton"
              style={{ height: 40 }}
            />
          ) : (
            <>
              {/* 내역이 전혀 없으면 온보딩 CTA 두 개(보내기 유도 + 받은 게
                  있을지도 모른다는 안내)를 함께 노출한다. */}
              {!hasActivity && claimableCount <= 0 && (
                <button
                  type="button"
                  className="wi-op-cta wi-op-cta--gradient"
                  onClick={handleSend}
                  data-track="onepop_send_first"
                >
                  Send your first POP!
                </button>
              )}
              {claimableCount > 0 ? (
                <button
                  type="button"
                  className="wi-op-cta wi-op-cta--purple"
                  onClick={handleClaim}
                  data-track="onepop_claim_banner"
                >
                  {`${claimableCount} POP${claimableCount === 1 ? "" : "s"} waiting to claim (total ${claimableTotal})`}
                </button>
              ) : (
                !hasActivity && (
                  // 아직 아무 내역이 없는 사용자는 공개 보드로 보내
                  // "받은 게 있을지도" 를 직접 확인하게 한다.
                  <button
                    type="button"
                    className="wi-op-cta wi-op-cta--purple"
                    onClick={handleBoard}
                    data-track="onepop_claim_maybe"
                  >
                    You might have POPs waiting
                  </button>
                )
              )}
              {repliesWaitingCount > 0 && (
                <button
                  type="button"
                  className="wi-op-cta wi-op-cta--outline"
                  onClick={handleActivity}
                  data-track="onepop_replies"
                >
                  {`${repliesWaitingCount} ${repliesWaitingCount === 1 ? "reply" : "replies"} waiting`}
                </button>
              )}
            </>
          )}
        </section>

        {hasActivity ? (
          <section className="wi-op-activity">
            <div className="wi-op-activity-head">
              <div className="wi-op-activity-heading">
                <span className="wi-op-activity-title">Recent activity</span>
                <span className="wi-op-activity-currency">
                  Currency: ONEUSD
                </span>
              </div>
              <button
                type="button"
                className="wi-op-link-btn"
                onClick={handleActivity}
                data-track="onepop_see_all_activity"
              >
                <span>See all activity</span>
                <OnePopChevronRightIcon size={16} />
              </button>
            </div>
            <ul className="wi-op-activity-list">
              {recentActivity.map((item) => (
                <ActivityRow key={item.id} item={item} />
              ))}
            </ul>
          </section>
        ) : (
          <section className="wi-op-features">
            <FeatureRow
              icon={<OnePopHandleIcon size={20} />}
              title="No wallet address needed"
              desc="just their @handle"
            />
            <FeatureRow
              icon={<OnePopInstantIcon size={20} />}
              title="Claim instantly"
              desc="social login, zero gas"
            />
            <FeatureRow
              icon={<OnePopRefundIcon size={20} />}
              title="Refund anytime"
              desc="get it back if unclaimed"
            />
          </section>
        )}
      </div>

      <div className="wi-op-footer">
        <span>Powered by ONEpop</span>
      </div>
    </>
  );
}

function FeatureRow({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <div className="wi-op-feature">
      <span className="wi-op-feature-icon" aria-hidden>
        {icon}
      </span>
      <span className="wi-op-feature-copy">
        <span className="wi-op-feature-title">{title}</span>
        <span className="wi-op-feature-desc">{desc}</span>
      </span>
    </div>
  );
}

function ActivityRow({ item }: { item: OnePopActivityItem }) {
  const handle = withAt(item.handle);
  const label =
    item.direction === "sent" ? `Sent to ${handle}` : `Claimed from ${handle}`;
  const initial = handle.replace("@", "").charAt(0).toUpperCase() || "?";

  return (
    <li className="wi-op-activity-item">
      <span className="wi-op-activity-avatar" aria-hidden>
        {item.avatarUrl ? (
          <img
            src={item.avatarUrl}
            alt=""
            width={30}
            height={30}
            draggable={false}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span className="wi-op-activity-initial">{initial}</span>
        )}
      </span>
      <span className="wi-op-activity-copy">
        <span className="wi-op-activity-meta">
          <span className="wi-op-activity-peer">{label}</span>
          <span className="wi-op-activity-sep" aria-hidden />
          <span className="wi-op-activity-time">
            {formatRelativeTime(item.timestamp)}
          </span>
        </span>
        <span className="wi-op-activity-amount">
          {formatAmount2(item.amountRaw, item.decimals)}
        </span>
      </span>
      <span
        className={cn("wi-op-badge", `wi-op-badge--${item.status}`)}
        data-status={item.status}
      >
        {STATUS_LABEL[item.status]}
      </span>
    </li>
  );
}
